import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiClient } from "app/configs/apiClient";
import dayjs from "dayjs";

// get review data
export const fetchReviews = createAsyncThunk(
  "/fetch/reviews",
  async ({ page = 1, limit = 10, selecetedDate, data, url, institutionGatewayDetailId }) => {
    const response = await apiClient().get(url, {
      params: {
        page: page,
        limit: limit,
        source: data?.source,
        search: data?.search,
        endDate: data?.endDate,
        isAnswered: data?.isAnswered,
        datePeriod: selecetedDate,
        startDate: data?.startDate,
        institutionGatewayDetailId: institutionGatewayDetailId ? institutionGatewayDetailId : data?.institutionGatewayDetailId,
      }
    });
    return response.data;
  }
);

// get Employees
export const getEmployees = createAsyncThunk(
  "/fetch/reviewsss",
  async () => {
    const response = await apiClient().get('employee/get-all');
    return response.data;
  }
);


// patch for comment adding
export const replyReview = createAsyncThunk(
  "review/reply",
  async (params, thunkAPI) => {
    const { id: reviewId, value } = params;

    const response = await apiClient().post(`/reviews/${reviewId}/reply`, {
      platform_id: "1",
      reply_comment: value,
    });
    return response.data;
  }
);

// reply to Facebook comments
export const replyFBComment = createAsyncThunk(
  "meta/comment/reply",
  async (params, thunkAPI) => {
    const { commentId, message, pageAccessToken } = params;

    const response = await apiClient().post(`meta/comment/reply`, {
      commentId: commentId,
      message: message,
      accessToken: pageAccessToken,
    });

    return {
      commentId,
      newReply: {
        id: response?.data?.data?.id,
        message,
        from: {
          name: response?.data?.data?.from?.name,
          id: response?.data?.data?.from?.id,
          picture: response?.data?.data?.from?.picture?.data?.url,
        },
        createdTime: response?.data?.data?.created_time,
      },
    };
  }
);

// delete Facebook reply
export const deleteFBReply = createAsyncThunk(
  "meta/comment",
  async ({ replyId, pageAccessToken }, thunkAPI) => {
    const response = await apiClient().delete(`meta/comment/${replyId}?accessToken=${pageAccessToken}`);
    return response.data;
  }
);

// Autogenerate Review using chatgpt public API
export const autoGenerateReviews = createAsyncThunk(
  "reviews",
  async (params, thunkAPI) => {
    const { id, value, styleValue, GR_name, rating } = params;

    const response = await apiClient().post(`/reviews/${id}/auto_reply`, {
      reviewText: value,
      style: styleValue ? styleValue : "simpler",
      reviewerName: GR_name,
      rating: rating
    });
    return response.data.response;
  }
);

// delete for whole review data
export const deleteComment = createAsyncThunk(
  "/delete/review",
  async ({ review_id, _id }, thunkAPI) => {

    const response = await apiClient().delete(`/reviews/${review_id}/reply/${_id}`, {
      data: {
        "platform_id": 1
      }
    });

    return { review_id, _id };
  }
);

// delete fro user comment
export const deleteUserReview = createAsyncThunk(
  "/delete/userReview",
  async (reviewId, thunkAPI) => {
    const response = await apiClient().delete(`/reviews/${reviewId}`);
    return response.data;
  }
);

const initialState = {
  reviews: [],
  review_error: null,
  total_reviews: 0,
};

const reviewSlice = createSlice({
  name: "message",
  initialState,
  reducers: {
    getReview(state, action) {
      return state.reviews;
    },
    updateReview(state, action) {
      state.reviews = [...action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReviews.fulfilled, (state, action) => {
      const apiReviews = action.payload.data;

      if (Array.isArray(apiReviews)) {
        // Create a map of existing reviews by review_id for quick lookup
        const existingReviewsMap = new Map(
          state.reviews.map(review => [review.review_id, review])
        );

        // Merge reviews: update if API update_time is newer, add new reviews
        state.reviews = apiReviews.map(apiReview => {
          const existingReview = existingReviewsMap.get(apiReview.review_id);

          if (existingReview) {
            // Compare update_time using dayjs
            const apiUpdateTime = dayjs(apiReview.update_time);
            const existingUpdateTime = dayjs(existingReview.update_time);

            // Only update if API's update_time is more recent
            if (apiUpdateTime.isAfter(existingUpdateTime)) {
              return apiReview;
            }
            return existingReview;
          }

          // If review doesn't exist in state, add it
          return apiReview;
        });
      } else {
        state.reviews = action.payload.data;
      }

      // Update error and total reviews count
      state.review_error = null;
      state.total_reviews = action.payload.total_reviews_count;
    });
    builder.addCase(fetchReviews.rejected, (state, action) => {
      state.reviews = [];
    });
    builder.addCase(deleteUserReview.fulfilled, (state, action) => { });
    builder.addCase(deleteUserReview.pending, (state, action) => {
    });
    builder.addCase(deleteUserReview.rejected, (state, action) => {
    });
    builder.addCase(deleteComment.fulfilled, (state, action) => {
      const { review_id, _id } = action.payload;
      const updatedReview = state.reviews.map((review) => {
        if (review.review_id == review_id) {
          review.reply_comment = review.reply_comment.filter((comment) => {
            return comment._id !== _id;
          });
        }
        return review;
      });
      state.reviews = updatedReview;
    });
    builder.addCase(deleteComment.pending, (state, action) => {
    });
    builder.addCase(deleteComment.rejected, (state, action) => {
    });
    builder.addCase(replyReview.fulfilled, (state, action) => {
      fetchReviews();
    });
    builder.addCase(replyReview.pending, (state, action) => {
    });
    builder.addCase(replyReview.rejected, (state, action) => {
    });
    builder.addCase(replyFBComment.fulfilled, (state, action) => {
      const { commentId, newReply } = action.payload;

      // Find the Facebook review containing the comment
      state.reviews = state.reviews.map((review) => {
        if (review.source === 'META' && review.metaComments?.some((comment) => comment.id === commentId)) {
          // Update the metaComments array immutably
          const updatedComments = review.metaComments.map((comment) => {
            if (comment.id === commentId) {
              return {
                ...comment,
                replies: [...(comment.replies || []), newReply], // Append new reply
              };
            }
            return comment;
          });
          return { ...review, metaComments: updatedComments };
        }
        return review;
      });
    });
    builder.addCase(replyFBComment.pending, (state, action) => {
    });
    builder.addCase(replyFBComment.rejected, (state, action) => {
    });
    builder.addCase(deleteFBReply.fulfilled, (state, action) => {
      const replyId = action.payload.data.id;

      // Iterate through the reviews array
      state.reviews = state.reviews.map((review) => {
        // Only process reviews with source = "META" and metaComments
        if (review.source === "META" && review.metaComments?.length > 0) {
          // Map through metaComments to update replies
          const updatedMetaComments = review.metaComments.map((comment) => {
            // Filter out the reply with the matching replyId
            const updatedReplies = comment.replies.filter(
              (reply) => reply.id !== replyId
            );
            // Return the comment with updated replies
            return {
              ...comment,
              replies: updatedReplies,
            };
          });

          // Return the review with updated metaComments
          return {
            ...review,
            metaComments: updatedMetaComments,
          };
        }
        // Return unchanged review if not META or no metaComments
        return review;
      });
    });
    builder.addCase(deleteFBReply.pending, (state, action) => {
    });
    builder.addCase(deleteFBReply.rejected, (state, action) => {
    });
  },
});

export default reviewSlice.reducer;